/**
 * Mash and fermentation schedule commands.
 *
 * Same four verbs as the ingredient lists, keyed on `stepId` instead of
 * `itemId`. Schedule order is *execution* order, not display preference, so
 * `reorder` really does change the beer.
 */

import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { minutes, num, temperature } from '../format';
import {
  idsOf,
  indexOfId,
  insertAt,
  itemNotFound,
  mergeDefined,
  priorValues,
  removeAt,
  reorderById,
  replaceAt,
  withRecipe,
} from '../helpers';
import * as p from '../payloads';

/** `invalid-state` for a reorder whose id list is not a permutation of the current one. */
function badPermutation(list: string) {
  return commandError(
    'invalid-state',
    `Reordering the ${list} needs the complete current set of ids, each exactly once.`,
  );
}

// ---------------------------------------------------------------------------
// Mash schedule
// ---------------------------------------------------------------------------

/** Add a mash step. */
export const recipeAddMashStep = defineCommand({
  type: 'recipe.addMashStep',
  payload: p.recipeAddMashStepPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const { step, index } = command.payload;
      if (indexOfId(recipe.mashSteps, 'stepId', step.stepId) !== -1) {
        return commandError('conflict', `A mash step with id "${step.stepId}" already exists.`);
      }
      return { ...recipe, mashSteps: insertAt(recipe.mashSteps, step, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.removeMashStep',
          { recipeId: prior.id, stepId: command.payload.step.stepId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => {
    const s = command.payload.step;
    return `Add mash step ${s.name}: ${temperature(s.targetTempC)} for ${minutes(s.timeMinutes)}`;
  },
});

/** Change fields of one mash step. */
export const recipeUpdateMashStep = defineCommand({
  type: 'recipe.updateMashStep',
  payload: p.recipeUpdateMashStepPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.mashSteps, 'stepId', command.payload.stepId);
      const step = recipe.mashSteps[index];
      if (step === undefined) return itemNotFound('mash step', command.payload.stepId);
      return {
        ...recipe,
        mashSteps: replaceAt(recipe.mashSteps, index, mergeDefined(step, command.payload.changes)),
      };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    const step = prior?.mashSteps.find((s) => s.stepId === command.payload.stepId);
    return prior === undefined || step === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.updateMashStep',
          {
            recipeId: prior.id,
            stepId: step.stepId,
            changes: priorValues(step, command.payload.changes),
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const step = state.recipes[command.payload.recipeId]?.mashSteps.find(
      (s) => s.stepId === command.payload.stepId,
    );
    const name = step?.name ?? command.payload.stepId;
    const temp = command.payload.changes.targetTempC;
    if (temp !== undefined) return `Set ${name} rest to ${temperature(temp)}`;
    const time = command.payload.changes.timeMinutes;
    if (time !== undefined) return `Set ${name} rest to ${minutes(time)}`;
    return `Update mash step ${name} (${Object.keys(command.payload.changes).sort().join(', ')})`;
  },
});

/** Remove one mash step. */
export const recipeRemoveMashStep = defineCommand({
  type: 'recipe.removeMashStep',
  payload: p.recipeRemoveMashStepPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.mashSteps, 'stepId', command.payload.stepId);
      if (index === -1) return itemNotFound('mash step', command.payload.stepId);
      return { ...recipe, mashSteps: removeAt(recipe.mashSteps, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    const index = indexOfId(prior.mashSteps, 'stepId', command.payload.stepId);
    const step = prior.mashSteps[index];
    if (step === undefined) return null;
    return inverseCommand(
      command,
      'recipe.addMashStep',
      { recipeId: prior.id, step, index },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const step = state.recipes[command.payload.recipeId]?.mashSteps.find(
      (s) => s.stepId === command.payload.stepId,
    );
    return `Remove mash step ${step?.name ?? command.payload.stepId}`;
  },
});

/** Reorder the mash schedule. */
export const recipeReorderMashSteps = defineCommand({
  type: 'recipe.reorderMashSteps',
  payload: p.recipeReorderMashStepsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const next = reorderById(recipe.mashSteps, 'stepId', command.payload.stepIds);
      if (next === null) return badPermutation('mash schedule');
      return { ...recipe, mashSteps: next };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.reorderMashSteps',
          { recipeId: prior.id, stepIds: idsOf(prior.mashSteps, 'stepId') },
          prior.updatedAt,
        );
  },
  summarize: () => 'Reorder the mash schedule',
});

// ---------------------------------------------------------------------------
// Fermentation schedule
// ---------------------------------------------------------------------------

/** Add a fermentation step. */
export const recipeAddFermentationStep = defineCommand({
  type: 'recipe.addFermentationStep',
  payload: p.recipeAddFermentationStepPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const { step, index } = command.payload;
      if (indexOfId(recipe.fermentationSteps, 'stepId', step.stepId) !== -1) {
        return commandError(
          'conflict',
          `A fermentation step with id "${step.stepId}" already exists.`,
        );
      }
      return { ...recipe, fermentationSteps: insertAt(recipe.fermentationSteps, step, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.removeFermentationStep',
          { recipeId: prior.id, stepId: command.payload.step.stepId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => {
    const s = command.payload.step;
    // The pressure only appears when there is one. A history line reading
    // "at 0 psi" would be describing every ordinary ferment ever recorded.
    const under = s.pressurePsi === null ? '' : ` under ${num(s.pressurePsi)} psi`;
    return `Add ${s.type} step ${s.name}: ${temperature(s.tempC)}${under} for ${num(s.days)} d`;
  },
});

/** Change fields of one fermentation step. */
export const recipeUpdateFermentationStep = defineCommand({
  type: 'recipe.updateFermentationStep',
  payload: p.recipeUpdateFermentationStepPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.fermentationSteps, 'stepId', command.payload.stepId);
      const step = recipe.fermentationSteps[index];
      if (step === undefined) return itemNotFound('fermentation step', command.payload.stepId);
      return {
        ...recipe,
        fermentationSteps: replaceAt(
          recipe.fermentationSteps,
          index,
          mergeDefined(step, command.payload.changes),
        ),
      };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    const step = prior?.fermentationSteps.find((s) => s.stepId === command.payload.stepId);
    return prior === undefined || step === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.updateFermentationStep',
          {
            recipeId: prior.id,
            stepId: step.stepId,
            changes: priorValues(step, command.payload.changes),
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const step = state.recipes[command.payload.recipeId]?.fermentationSteps.find(
      (s) => s.stepId === command.payload.stepId,
    );
    const name = step?.name ?? command.payload.stepId;
    const temp = command.payload.changes.tempC;
    if (temp !== undefined) return `Set ${name} to ${temperature(temp)}`;
    const end = command.payload.changes.endTempC;
    if (end !== undefined) {
      return end === null
        ? `Hold ${name} at a constant temperature`
        : `Ramp ${name} to ${temperature(end)}`;
    }
    const days = command.payload.changes.days;
    if (days !== undefined) return `Set ${name} to ${num(days)} d`;
    const pressure = command.payload.changes.pressurePsi;
    if (pressure !== undefined) {
      return pressure === null
        ? `Vent ${name} to atmosphere`
        : `Hold ${name} at ${num(pressure)} psi`;
    }
    return `Update ${name} (${Object.keys(command.payload.changes).sort().join(', ')})`;
  },
});

/** Remove one fermentation step. */
export const recipeRemoveFermentationStep = defineCommand({
  type: 'recipe.removeFermentationStep',
  payload: p.recipeRemoveFermentationStepPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const index = indexOfId(recipe.fermentationSteps, 'stepId', command.payload.stepId);
      if (index === -1) return itemNotFound('fermentation step', command.payload.stepId);
      return { ...recipe, fermentationSteps: removeAt(recipe.fermentationSteps, index) };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    const index = indexOfId(prior.fermentationSteps, 'stepId', command.payload.stepId);
    const step = prior.fermentationSteps[index];
    if (step === undefined) return null;
    return inverseCommand(
      command,
      'recipe.addFermentationStep',
      { recipeId: prior.id, step, index },
      prior.updatedAt,
    );
  },
  summarize: (state, command) => {
    const step = state.recipes[command.payload.recipeId]?.fermentationSteps.find(
      (s) => s.stepId === command.payload.stepId,
    );
    return `Remove fermentation step ${step?.name ?? command.payload.stepId}`;
  },
});

/** Reorder the fermentation schedule. */
export const recipeReorderFermentationSteps = defineCommand({
  type: 'recipe.reorderFermentationSteps',
  payload: p.recipeReorderFermentationStepsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const next = reorderById(recipe.fermentationSteps, 'stepId', command.payload.stepIds);
      if (next === null) return badPermutation('fermentation schedule');
      return { ...recipe, fermentationSteps: next };
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.reorderFermentationSteps',
          { recipeId: prior.id, stepIds: idsOf(prior.fermentationSteps, 'stepId') },
          prior.updatedAt,
        );
  },
  summarize: () => 'Reorder the fermentation schedule',
});
